/**
 * Amplify configuration — resolved at runtime, not build time.
 *
 * Server-side: reads directly from process.env (ECS task env vars).
 * Client-side: fetched from /api/config on first load.
 *
 * Identity (pool ID, client ID, auth domain) has no hardcoded fallback. A
 * previous fallback pointed at `us-east-1_B641uVFwb`, which is not a live pool
 * in the commercial edge account — missing env vars therefore looked configured
 * and failed as opaque Cognito rejections. Production builds now fail closed
 * (same NODE_ENV gate as lib/launch-darkly.ts). For local `next dev`, copy
 * local_dev/.env.local.example to local_dev/.env.local.dev — that is the file
 * `npm run dev` loads (see local_dev/run-next-profile.sh).
 */

import type { ResourcesConfig } from "aws-amplify";
import { resolveCognitoAuthHost } from "@/lib/cognito-auth-host";

const IDENTITY_ENV_VARS = [
  "COGNITO_POOL_ID",
  "COGNITO_CLIENT_ID",
  "COGNITO_AUTH_DOMAIN",
] as const;

type IdentityEnvVar = (typeof IDENTITY_ENV_VARS)[number];

export interface CognitoConfig {
  userPoolId: string;
  userPoolClientId: string;
  /** Hosted-UI domain *prefix* (e.g. `tranquility-ai-commercial-dev`). */
  cognitoDomain: string;
  cognitoRegion: string;
  /**
   * Fully-qualified hosted-UI / OAuth host. Partition-correct: `auth-fips` on
   * GovCloud-based partitions, `auth` on commercial. Always use this to build
   * Cognito URLs — never re-assemble it from `cognitoDomain` + `cognitoRegion`.
   */
  cognitoAuthHost: string;
  appUrl: string;
}

/**
 * @param origins - Every origin users may sign in on (see getAllowedOrigins).
 *   The Amplify adapter picks the redirect URL that starts with its runner's
 *   origin, so each one needs its own entry. Defaults to ``cfg.appUrl``.
 */
export function buildAmplifyConfig(
  cfg: CognitoConfig,
  origins: string[] = [cfg.appUrl],
): ResourcesConfig {
  return {
    Auth: {
      Cognito: {
        userPoolId: cfg.userPoolId,
        userPoolClientId: cfg.userPoolClientId,
        loginWith: {
          oauth: {
            domain: cfg.cognitoAuthHost,
            scopes: ["openid", "email", "profile"],
            redirectSignIn: origins.map((o) => `${o}/api/auth/sign-in-callback`),
            redirectSignOut: origins.map((o) => `${o}/api/auth/sign-out-callback`),
            responseType: "code",
          },
        },
      },
    },
  };
}

function requiredIdentityEnv(name: IdentityEnvVar): string {
  const value = process.env[name]?.trim();
  if (value) {
    return value;
  }

  // Root layout reads this during `next build`. ECS injects the real values at
  // task start, not image build — do not fail the Docker build.
  if (process.env.NEXT_PHASE === "phase-production-build") {
    return "";
  }

  if (process.env.NODE_ENV === "production") {
    throw new Error(
      `${name} is not set. Refusing to serve Cognito identity config in a ` +
        "production build rather than silently using a hardcoded pool. Set " +
        `${name} via infra (infra/main.py).`,
    );
  }

  if (process.env.NODE_ENV === "test") {
    return "";
  }

  throw new Error(
    `${name} is not set. Copy containers/simple/local_dev/.env.local.example ` +
      "to containers/simple/local_dev/.env.local.dev (the file npm run dev " +
      "loads; the hosted UI client there already registers " +
      "http://localhost:3000/api/auth/sign-in-callback).",
  );
}

/** Server-side config — reads from process.env. No hardcoded identity fallback. */
export function getServerConfig(): CognitoConfig {
  const cognitoDomain = requiredIdentityEnv("COGNITO_AUTH_DOMAIN");
  // COGNITO_REGION is set by CellFrontendService to the partition primary
  // region (where the user pool actually lives), NOT the cell's region.
  // Cognito user pools are partition-wide, so for cross-region cells (EU,
  // govcloud-east) the hosted-UI domain only exists in the primary region:
  //   {prefix}.auth.us-east-1.amazoncognito.com  ✓
  //   {prefix}.auth.eu-central-1.amazoncognito.com  ✗ (doesn't exist)
  // AWS_REGION is the ECS task's runtime region (cell region) — kept as
  // fallback for older deploys where COGNITO_REGION wasn't yet set.
  // ``||`` (not ``??``) so an empty-string misconfiguration falls through
  // to the next option instead of being passed as a valid-but-broken value.
  const cognitoRegion =
    process.env.COGNITO_REGION
    || process.env.AWS_REGION
    || process.env.AWS_DEFAULT_REGION
    || "us-east-1";
  return {
    userPoolId: requiredIdentityEnv("COGNITO_POOL_ID"),
    userPoolClientId: requiredIdentityEnv("COGNITO_CLIENT_ID"),
    cognitoDomain,
    cognitoRegion,
    // COGNITO_AUTH_HOST is the partition-correct hosted-UI FQDN injected by
    // CellFrontendService / EdgeFrontendService. On GovCloud-based partitions
    // the hosted UI is FIPS-only (``auth-fips``) and the assembled ``auth.``
    // fallback does not resolve at all — see lib/cognito-auth-host.ts.
    cognitoAuthHost: resolveCognitoAuthHost(
      process.env.COGNITO_AUTH_HOST,
      cognitoDomain,
      cognitoRegion,
    ),
    appUrl: process.env.AMPLIFY_APP_ORIGIN || "http://localhost:3000",
  };
}

// No default export — use getServerConfig() + buildAmplifyConfig() lazily
// to avoid evaluating empty env vars at build time.
